/* eslint-disable spaced-comment */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import PropTypes from 'prop-types';
import {
  useAuth,
  coreServiceAutoOrgPermissionChecker
} from 'utilities/base-auth';
import useOrganization from 'hooks/useOrganization';
import useOrgWorkspaceId from 'hooks/useOrgWorkspaceId';
import { useHistory, useLocation } from 'react-router-dom';

import { Spinner } from '@agconnections/grow-ui';

import Navigation from 'components/Navigation';
import ErrorHandlerWithRouter from 'components/ErrorHandler';
import setOrgAndPermission from 'helpers/setOrgAndPermission';
import { Context } from 'components/Store';
import { IS_FULL_SCREEN_NEEDED } from 'reducers/reducer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import getPreviousValue from 'helpers/findPreviousValue';
import { paths } from 'routes/paths';
import { useToast } from 'components/ToastContainer';
import useOrganizations from 'hooks/useOrganizations';
import OrgGlobalLoader from 'components/OrgGlobalLoader';
import OrgApiFailure from 'components/ErrorHandler/OrganizationApiFailure/OrgApiFailure';
import TopNavigation from './components/TopNavigation';
import './index.css';

const returnData = (value1, value2) => {
  if (value1) return value1;
  if (value2) return [value2];
  return [];
};

const FULL_SCREEN_PAGES = [
  'property-landing',
  'applications/create',
  'applications',
  'work-orders',
  'reports',
  'crop-zone-matcher',
  'products',
  'storage-locations',
  'sale-locations',
  'loads',
  'yieldV2/create',
  'yieldV2/commodity-summary'
];

const EMBEDDED_ROUTES = [
  `${paths.yieldV2}/storage-locations`,
  `${paths.yieldV2}/sale-locations`,
  `${paths.yieldV2}/loads`,
  `${paths.yieldV2}/commodity-summary`
];

const ROUTES_TO_EXCLUDE = ['/app/welcome'];

const SCROLLBAR_PATHNAME_LIST = [
  '/app/crop-seasons',
  '/app/organizations',
  '/app/property-landing',
  '/app/work-orders',
  '/app/applications'
];

const AppShell = ({ routes, children }) => {
  const [
    { organization, isFullScreenNeeded, loadingOrg: LoadingOrgAndPermission },
    dispatch
  ] = useContext(Context);
  const { user } = useAuth();
  const toast = useToast();
  const { getDefaultOrg, loadedDefaultOrg, defaultOrg } = useOrganizations();
  const {
    setOrganization,
    organizationPermissionError,
    getOrgByIdV1,
    loadedOrgByIdV1,
    orgByIdV1
  } = useOrganization();
  const { getOrgWorkspaceId } = useOrgWorkspaceId();
  const [stateLoaded, setStateLoaded] = useState(false);
  const [error, setError] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { releaseFe50Orgs } = useFlags();
  const redirectToWelcome = !user;
  const APILoaded = loadedDefaultOrg || loadedOrgByIdV1;
  const orgIds = useRef(new Set());
  const validateLoop = useRef('');
  const bootstrapTried = useRef(false);

  const pushToWelcome = () => {
    history.replace(paths.welcome);
    setStateLoaded(true);
    orgIds.current = new Set();
    validateLoop.current = '';
  };

  const pushToOrgApiFailure = () => {
    setError(true);
  };

  // New users get an auto-created org but no CWF roles, so the orgs endpoint
  // returns nothing. Grant the roles once here, right after the orgs load; the
  // caller then retries loading the orgs so the now-permitted org resolves.
  // Best-effort — failures are logged and the retry falls through to the
  // existing org API failure handling.
  const grantCwfRoles = async currentUser => {
    try {
      const workspaceId = await getOrgWorkspaceId(
        currentUser.auto_created_org_id
      );
      if (workspaceId) {
        await coreServiceAutoOrgPermissionChecker(
          currentUser.auto_created_org_id,
          workspaceId,
          currentUser.id
        );
      } else {
        datadogLogs.logger.error('No workspace id found for auto-created org', {
          userId: currentUser.id,
          autoCreatedOrgId: currentUser.auto_created_org_id
        });
      }
    } catch (err) {
      datadogLogs.logger.error('Error granting CWF roles to auto-created org', {
        error: err,
        userId: currentUser.id,
        autoCreatedOrgId: currentUser.auto_created_org_id
      });
      // swallow the error so the promise doesn't reject unhandled
    }
  };

  useEffect(() => {
    if (!releaseFe50Orgs) {
      getDefaultOrg(0, 50);
      return;
    }
    const selectedOrganizationId = localStorage.getItem(
      'selectedOrganizationId'
    );

    if (selectedOrganizationId) {
      getOrgByIdV1(selectedOrganizationId);
      return;
    }
    getDefaultOrg(0, 1);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isOrgFailingAPI = organizationPermissionError;
    if (!releaseFe50Orgs && isOrgFailingAPI) {
      toast.error('Unable to load organization data', {
        supportButton: true,
        content:
          "An error prevented us from loading your list of organization's. Please try again."
      });
      pushToOrgApiFailure();
    }
    if (!releaseFe50Orgs || !isOrgFailingAPI) return;

    if (orgIds.current.size <= 1) {
      toast.error('Unable to load organization data', {
        supportButton: true,
        content:
          "An error prevented us from loading your list of organization's. Please try again."
      });
      pushToOrgApiFailure();
      return;
    }

    const previousSelectedOrg = getPreviousValue(orgIds.current);
    if (validateLoop.current === previousSelectedOrg) {
      toast.error('Unable to load organization data', {
        supportButton: true,
        content:
          "An error prevented us from loading any of your organization's. Please try logging in again."
      });
      pushToOrgApiFailure();
      return;
    }
    toast.error('Unable to load organization data', {
      supportButton: true,
      content: `An error prevented us from loading your organization ${organization.name}. Please try again.`
    });
    validateLoop.current = previousSelectedOrg;
    getOrgByIdV1(previousSelectedOrg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationPermissionError]);

  useEffect(() => {
    if (!APILoaded) {
      return;
    }
    if (redirectToWelcome && location.pathname !== paths.welcome) {
      pushToWelcome();
      return;
    }
    if (!orgByIdV1 && !defaultOrg?.length) {
      if (user?.auto_created_org_id && !bootstrapTried.current) {
        bootstrapTried.current = true;
        // Grant the roles, then retry loading the orgs so the now-permitted
        // auto-created org resolves. bootstrapTried guards against a loop — a
        // still-empty retry falls through to the failure handling below.
        grantCwfRoles(user).then(() =>
          getDefaultOrg(0, releaseFe50Orgs ? 1 : 50)
        );
        return;
      }
      pushToOrgApiFailure();
      return;
    }

    setOrgAndPermission(
      user,
      // getOrganizationData(organizationV2, organizationsV2),
      returnData(defaultOrg, orgByIdV1),
      dispatch,
      setStateLoaded
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [APILoaded, defaultOrg, orgByIdV1]);

  useEffect(() => {
    const path = location.pathname.split('/');
    const payload = FULL_SCREEN_PAGES.some(page => {
      const isCompositePath = page.includes('/');
      if (isCompositePath) return location.pathname.includes(page);
      return path.includes(page);
    });
    dispatch({
      type: IS_FULL_SCREEN_NEEDED,
      payload
    });
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (organization.id !== "9a1a1673-6926-41fa-9d71-a5aad6f67cb6") {
      orgIds.current.add(organization.id);
      setOrganization(organization);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const getScrollbarClass = () => {
    return SCROLLBAR_PATHNAME_LIST.some(path =>
      location.pathname.includes(path)
    )
      ? 'config-scrollbar'
      : '';
  };

  // Apply scrollbar styles for routes in SCROLLBAR_PATHNAME_LIST
  // Apply to html, body, and main elements to cover all scrolling containers
  useEffect(() => {
    const shouldApplyScrollbarClass = SCROLLBAR_PATHNAME_LIST.some(path =>
      location.pathname.includes(path)
    );

    if (shouldApplyScrollbarClass) {
      // Add class to body and html elements
      document.body.classList.add('config-scrollbar');
      document.documentElement.classList.add('config-scrollbar');

      // Create or update inline style to override global scrollbar
      // This ensures our styles load last and take precedence
      let styleElement = document.getElementById('config-scrollbar-override');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'config-scrollbar-override';
        document.head.appendChild(styleElement);
      }

      // Target html, body, main, and any element with config-scrollbar class
      // Use maximum specificity and also target global scrollbar
      styleElement.textContent = `
        /* Global override for all scrollbars on these routes */
        ::-webkit-scrollbar {
          height: 8px !important;
          width: 8px !important;
          background: #fff !important;
        }
        ::-webkit-scrollbar-track {
          background: #fff !important;
        }
        ::-webkit-scrollbar-thumb {
          background: #888 !important;
          border-radius: 4px !important;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #555 !important;
        }

        /* Specific targeting with config-scrollbar class */
        html.config-scrollbar::-webkit-scrollbar,
        html body.config-scrollbar::-webkit-scrollbar,
        body.config-scrollbar::-webkit-scrollbar,
        html main.config-scrollbar::-webkit-scrollbar,
        main.config-scrollbar::-webkit-scrollbar,
        html .config-scrollbar::-webkit-scrollbar,
        .config-scrollbar::-webkit-scrollbar {
          height: 8px !important;
          width: 8px !important;
          background: #fff !important;
        }
        html.config-scrollbar::-webkit-scrollbar-track,
        html body.config-scrollbar::-webkit-scrollbar-track,
        body.config-scrollbar::-webkit-scrollbar-track,
        html main.config-scrollbar::-webkit-scrollbar-track,
        main.config-scrollbar::-webkit-scrollbar-track,
        html .config-scrollbar::-webkit-scrollbar-track,
        .config-scrollbar::-webkit-scrollbar-track {
          background: #fff !important;
        }
        html.config-scrollbar::-webkit-scrollbar-thumb,
        html body.config-scrollbar::-webkit-scrollbar-thumb,
        body.config-scrollbar::-webkit-scrollbar-thumb,
        html main.config-scrollbar::-webkit-scrollbar-thumb,
        main.config-scrollbar::-webkit-scrollbar-thumb,
        html .config-scrollbar::-webkit-scrollbar-thumb,
        .config-scrollbar::-webkit-scrollbar-thumb {
          background: #888 !important;
          border-radius: 4px !important;
        }
        html.config-scrollbar::-webkit-scrollbar-thumb:hover,
        html body.config-scrollbar::-webkit-scrollbar-thumb:hover,
        body.config-scrollbar::-webkit-scrollbar-thumb:hover,
        html main.config-scrollbar::-webkit-scrollbar-thumb:hover,
        main.config-scrollbar::-webkit-scrollbar-thumb:hover,
        html .config-scrollbar::-webkit-scrollbar-thumb:hover,
        .config-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555 !important;
        }

        /* Firefox support */
        * {
          scrollbar-width: thin !important;
          scrollbar-color: #888 #fff !important;
        }
        html.config-scrollbar,
        html body.config-scrollbar,
        body.config-scrollbar,
        html main.config-scrollbar,
        main.config-scrollbar,
        html .config-scrollbar,
        .config-scrollbar {
          scrollbar-width: thin !important;
          scrollbar-color: #888 #fff !important;
        }
      `;
    } else {
      // Remove class from body and html
      document.body.classList.remove('config-scrollbar');
      document.documentElement.classList.remove('config-scrollbar');

      // Remove the override style when not needed
      const styleElement = document.getElementById('config-scrollbar-override');
      if (styleElement) {
        styleElement.remove();
      }
    }

    // Cleanup: remove classes and style when component unmounts
    return () => {
      document.body.classList.remove('config-scrollbar');
      document.documentElement.classList.remove('config-scrollbar');
      const styleElement = document.getElementById('config-scrollbar-override');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [location.pathname]);

  return (
    <>
      {error ? (
        <OrgApiFailure />
      ) : (
        <div className="flex h-screen bg-[#F8F9FA]">
          <Navigation routes={routes} />
          <div className="flex flex-col flex-1 w-0">
            {stateLoaded ? (
              <>
                <TopNavigation />
                <main
                  className={`relative overflow-auto flex flex-1 focus:outline-none bg-app-background ${getScrollbarClass()}`}
                  style={{ height: 'calc(100% - 64px)' }}
                >
                  <div
                    className={`w-full flex flex-col flex-1 ${!isFullScreenNeeded &&
                      'p-4'} mx-auto`}
                  >
                    <ErrorHandlerWithRouter>{children}</ErrorHandlerWithRouter>
                    {releaseFe50Orgs && (
                      <OrgGlobalLoader
                        route={location.pathname}
                        shouldRenderInRoutes={[
                          ...Object.values(paths),
                          ...EMBEDDED_ROUTES
                        ].filter(route => !ROUTES_TO_EXCLUDE.includes(route))}
                        isLoading={LoadingOrgAndPermission}
                      />
                    )}
                  </div>
                </main>
              </>
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      )}
    </>
  );
};

AppShell.defaultProps = {
  children: null
};

AppShell.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  children: PropTypes.element
};

export default AppShell;
